// React i hooks
import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

// API servisi
import { deleteUserAPI, getUserAPI } from "services/api/users/api";
import { UserDetailFormValues } from "services/api/users/types";
import { initialUserDetailForm } from "services/api/users/consts";
import { Errors } from "services/forms/validation/types";
import { Routes as UserRoutes } from "ui/modules/users/navigation";

// Tipovi i konstante
import { UserDetailPageCmp, UserDetailPageProps } from "./types";
import { USER_DETAIL_PAGE_TITLE } from "ui/components/PageTitle/consts";
import { BreadcrumbItem } from "ui/components/Breadcrumb/types";
import { SIDEBAR_ITEM_USER } from "app/components/Sidebar/consts";

// UI komponente
import { Breadcrumb } from "ui/components/Breadcrumb";
import { PageHeader } from "ui/components/PageHeader";
import { PageTitle } from "ui/components/PageTitle";
import UserDetailCard from "./components/UserDetailCard/UserDetailCard";
// import TabMenu from "../components/TabMenu/TabMenu";

// Moduli specifični za projekt
import { Routes } from "../../navigation";
import TabMenu from "../components/TabMenu/TabMenu";
import { useTranslate } from "helpers";
import { Button } from "ui/components/Button/Button";
import Loader from "ui/components/Loader/Loader";
import { Dialog } from "ui/components/Dialog";

const UserDetailPage: UserDetailPageCmp = (props: UserDetailPageProps) => {
  const { id } = useParams();



  const { t } = useTranslate();

  const [errors, setErrors] = useState<Errors>({});
  const [userId, setUserId] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [openDialog, setOpenDialog] = useState(false);

  const [user, setUser] = useState<UserDetailFormValues>(initialUserDetailForm);

  const navigate = useNavigate();

  const title = USER_DETAIL_PAGE_TITLE;

  useEffect(() => {
    if (id) fetchUser(id);
  }, [id]);

  const fetchUser = async (id: string) => {
    if (id === undefined) {
      setUser(initialUserDetailForm);
      return;
    }

    setIsLoading(true);

    let result;
    try {
      result = await getUserAPI(id);

      setUser(result);
    } catch {
      setIsLoading(false);
      return;
    }

    setUser(result);
    setIsLoading(false);

    setErrors({});
  };

  const breadcrumbs: BreadcrumbItem[] = [
    { text: SIDEBAR_ITEM_USER.text, route: SIDEBAR_ITEM_USER.route },
  ];

  const handleDeleteClick = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>, id: string) => {
    event.stopPropagation();
    event.preventDefault();

    setUserId(id);
    setOpenDialog(true);
  }

  const handleDeleteDialogConfirm = useCallback(async () => {
    if (!userId) return;

    try {
      const deleted = await deleteUserAPI(
        userId,
        t
      );

      if (deleted) {
        navigate(UserRoutes.UserSearchPageRouter);
      }

      // zatvori dijalog nakon brisanja
      setOpenDialog(false);
      setUserId("");
    } catch (err) {
      console.error("Greška prilikom brisanja korisnika:", err);
    }
  }, [userId, t]);

  const handleDeleteDialogClose = useCallback(() => {
    setUserId("");
    setOpenDialog(false);
  }, []);


  if (!id) return <></>;

  if (isLoading) return <Loader isLoading={isLoading}>{null}</Loader>;

  return (
    <>
      <div className="">
        <div className="bg-white rounded-t-md">
          <PageHeader>
            <div className="flex justify-between">
              <PageTitle
                title={`${t(title.text)} [${user.firstName} ${user.lastName}] `}
                icon={title.icon}
                iconColor={title.iconColor}
              />
              <Button
                size="large"
                variant="remove"
                children={t("admin.user.remove")}
                onClick={(e) => handleDeleteClick(e, id)}
                startIcon="/media/icons/trash.svg"
              />
            </div>
            <div className="flex justify-between">
              <Breadcrumb items={breadcrumbs} />
            </div>
          </PageHeader>
          <TabMenu userId={id} />
        </div>
        <div className="px-4 py-4">
          <UserDetailCard user={user} fetchUser={fetchUser} />
        </div>
      </div>
      <Dialog
        open={openDialog}
        title={t(
          "admin.user.dialog.delete.title"
        )}
        message={t(
          "admin.user.dialog.delete.message"
        )}
        confirmText={t(
          "admin.user.dialog.delete.confirmtext"
        )}
        cancelText={t(
          "admin.user.dialog.delete.canceltext"
        )}
        onConfirm={handleDeleteDialogConfirm}
        onCancel={handleDeleteDialogClose}
      />
    </>
  );
};

UserDetailPage.route = Routes.UserDetailPageRouter;

export default UserDetailPage;
